import Cards from "../../../common/Cards";
import Input from "../../../form/Input";
import Select from "../../../form/select/Select";
import DatePicker from "../../../form/DatePicker";
import Button from "../../../form/Button";
import { countries } from "../../../../db/countries";
import { initialsValue, loadDataOnScroll, removeDuplicateFromArray, replaceOldData } from "../../../../helpers";
import { useEffect, useState } from "react";
import AvatarUpload from "../../../common/AvatarUpload";
import useForm from "../../../../hooks/useForm";
import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import { userAddEdit, userDetails } from "../../../../services/userService";
import Form from "../../../form/Form";
import { countryList } from "../../../../services/countryService";
import { languageList } from "../../../../services/LanguageService";

const validation = {
  firstName: { required: true, message: "First name is required" },
  // dob: { required: true, message: "DOB is required" },
  // lastName: { required: true, message: "Last name is required" },
  // gender: { required: true, message: "Gender is required" },
  // email: { required: true, message: "Email is required" },
  // role: { required: true, message: "Role is required" },
  // zipcode: { required: true, message: "Zipcode is required" },
};

const UserProfile = ({ details, loading, getResponse = () => {} }) => {
  const [keyword, setKeyword] = useState("");
  const [countryKeyword, setCountryKeyword] = useState("");
  const [LanguageKeyword, setLanguageKeyword] = useState("");
  const [countryFilter, setCountryFilter] = useState({
    limit: 20,
    page: 1,
  });
  const [LanguageFilter, setLanguageFilter] = useState({
    limit: 20,
    page: 1,
  });

  const { values, handleChange, handleSubmit, errors, setFieldsValue, resetField, formValidate } = useForm({
    validationRules: validation,
  });

  const { data: country } = useQuery({
    queryKey: ["country", countryKeyword, countryFilter],
    queryFn: async () => {
      const res = await countryList({
        ...countryFilter,
        ...(countryKeyword && { keyword: countryKeyword }),
      });
      if (countryFilter?.page > 1) {
        const marge_arr = [...(country?.data || []), ...res?.docs?.data];
        return {
          ...res?.docs,
          data: replaceOldData({
            oldData: marge_arr,
            newData: res?.docs?.data,
          }),
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  const { data: language } = useQuery({
    queryKey: ["language", LanguageKeyword, LanguageFilter],
    queryFn: async () => {
      const res = await languageList({
        ...LanguageFilter,
        ...(LanguageKeyword && { keyword: LanguageKeyword }),
      });

      if (LanguageFilter?.page > 1) {
        const marge_arr = [...(language?.data || []), ...res?.docs?.data];
        return {
          ...res?.docs,
          data: replaceOldData({
            oldData: marge_arr,
            newData: res?.docs?.data,
          }),
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (details?._id) {
      delete validation?.confirmPassword;
      delete validation?.password;
      setFieldsValue({
        ...details,
        ...details?.address,
        image: details?.image?.url,
        language: details?.language?._id,
        address: Array.isArray(details?.address) ? details?.address?.[0] : details?.address,
      });
    }
    return () => {
      setFieldsValue({});
    };
  }, [details?._id]);

  const {
    mutate: onSubmit,
    error,
    isPending: isLoading,
  } = useMutation({
    mutationFn: async (val) => {
      const address = {
        city: val?.city,
        country: val?.country,
        state: val?.state,
        zipcode: val?.zipcode,
      };
      const { ...rest } = val;
      delete rest?._id;
      return await userAddEdit(
        details?._id
          ? {
              ...(details?._id && {
                id: details?._id,
                ...rest,
                address: [address],
              }),
            }
          : {
              ...val,
              address: [address],
            }
      );
    },
    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        resetField();
      }
      return res;
    },
    onError: (error) => {
      console.log(error);
    },
  });

  return (
    <Form onSubmit={onSubmit} handleSubmit={handleSubmit} loading={loading}>
      <div className="px-6 flex items-center justify-center relative">
        <AvatarUpload
          src={values?.image instanceof File ? URL.createObjectURL(values?.image) : values?.image}
          onChange={(file) => {
            handleChange({ target: { name: "image", value: file } });
          }}
          alt="profile"
          initial={initialsValue(details?.firstName + " " + details?.lastName)}
        />
      </div>
      <Cards
        title="Basic information"
        description="This section includes essential details and other personal identifiers that help establish your identity."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={"First Name"}
              inputPlaceholder="Enter first name"
              onChange={handleChange}
              inputName={"firstName"}
              value={values?.firstName}
              {...formValidate(errors, "firstName")}
            />
            <Input
              label={"Last Name"}
              inputPlaceholder="Enter last name"
              inputName={"lastName"}
              value={values?.lastName}
              onChange={handleChange}
              {...formValidate(errors, "lastName")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DatePicker
              label={"Select DOB"}
              position="fixed"
              onChange={(e) =>
                handleChange({
                  target: { name: "dob", value: e?.startDate || e?.endDate },
                })
              }
              value={{ startDate: values?.dob, endDate: values?.dob }}
              {...formValidate(errors, "dob")}
            />
            <Input
              label={"Email"}
              inputType={"email"}
              inputPlaceholder="Enter email address"
              inputName={"email"}
              value={values?.email}
              onChange={handleChange}
              isDisabled={true}
              {...formValidate(errors, "email")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label={"Gender"}
              placeholder={"Select gender"}
              onChange={(e) => handleChange({ target: { name: "gender", value: e } })}
              selectedValue={values?.gender}
              dropdownData={[
                { name: "Male", value: "male" },
                { name: "Female", value: "female" },
                { name: "Others", value: "others" },
              ]}
              {...formValidate(errors, "gender")}
            />
            <Select
              label={"Role"}
              placeholder={"Select role"}
              onChange={(e) => {
                console.log(e);
                handleChange({ target: { name: "role", value: e } });
              }}
              selectedValue={values?.role}
              dropdownData={[
                { name: "Admin", value: "admin" },
                { name: "User", value: "user" },
                { name: "Organization", value: "organization" },
              ]}
              {...formValidate(errors, "role")}
            />
            {/* <Select
              label={"Language"}
              placeholder={"Select language"}
              isSearch
              onSearchHandler={setKeyword}
              onChange={(e) =>
                handleChange({ target: { name: "language", value: e } })
              }
              selectedValue={values?.language}
              dropdownData={language?.data?.map((lang) => {
                return {
                  name: lang?.name,
                  value: lang?._id,
                };
              })}
              onScroll={(e) => {
                loadDataOnScroll({
                  event: e,
                  metadata: language?.metadata,
                  setData: setLanguageFilter,
                });
              }}
              {...formValidate(errors, "language")}
            /> */}
          </div>
        </div>
      </Cards>
      {/* <Cards
        title="Contact details"
        description="This section contains the information required to reach you."
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            
            <Input
              label={"Phone Number"}
              inputPlaceholder="Enter phone number"
              inputName={"phone"}
              value={values?.phone}
              onChange={handleChange}
              {...formValidate(errors, "phone")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label={"Country"}
              isSearch
              onSearchHandler={setCountryKeyword}
              placeholder={"Select country"}
              onChange={(e) =>
                handleChange({ target: { name: "country", value: e } })
              }
              selectedValue={values?.country}
              dropdownData={country?.data?.map((cn) => {
                return {
                  name: cn?.name,
                  value: cn?.name,
                  image: countries?.find((c) => c?.cca3 === cn?.iso3)?.flags
                    ?.svg,
                };
              })}
              onScroll={(e) => {
                loadDataOnScroll({
                  event: e,
                  metadata: country?.metadata,
                  setData: setCountryFilter,
                });
              }}
              {...formValidate(errors, "country")}
            />
            <Input
              label={"City"}
              inputPlaceholder="Enter city"
              inputName={"city"}
              value={values?.city}
              onChange={handleChange}
              {...formValidate(errors, "city")}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={"State"}
              inputPlaceholder="Enter state"
              inputName={"state"}
              value={values?.state}
              onChange={handleChange}
              {...formValidate(errors, "state")}
            />
            <Input
              label={"Zipcode"}
              inputName={"zipcode"}
              value={values?.zipcode}
              inputPlaceholder="Enter zipcode"
              onChange={handleChange}
              {...formValidate(errors, "zipcode")}
            />
          </div>
        </div>
      </Cards> */}
      <div className="pt-3 pb-6 flex justify-end">
        <Button
          buttonType="submit"
          buttonClasses={"min-w-36"}
          buttonLabel={details?._id ? "Update User" : "Save User"}
          {...((isLoading || loading) && {
            loading: true,
            buttonIconPosition: "left",
            isDisable: true,
          })}
        />
      </div>
    </Form>
  );
};

export default UserProfile;
